import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { PageLoader } from "../../components/PageLoader.tsx";
import {
  IconCamera,
  IconAlertTriangle,
  IconCheck,
  IconTicket,
  IconCopy,
  IconChevronRight,
} from "../../components/Icons.tsx";
import { useOrder } from "@/features/orders/hooks";
import {
  usePaymentRequisites,
  useSelectPaymentCurrency,
} from "@/features/payment/hooks";
import { getErrorMessage } from "../../lib/graphql.ts";
import {
  getTelegram,
  haptic,
  safeShowAlert,
} from "../../lib/telegram.ts";
import { config } from "../../config.ts";
import { getAuthHeaders } from "@/lib/api-headers";

const CARD_CLASS =
  "rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50";

export function PaymentPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const tg = getTelegram();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // URQL hooks
  const {
    order,
    isLoading: isOrderLoading,
    isFetching: isOrderFetching,
    error: orderError,
    refetch: refetchOrder,
  } = useOrder(orderId, { pollInterval: 5000 });
  const { requisites, isLoading: isRequisitesLoading } = usePaymentRequisites();
  const { selectCurrency, isLoading: isChangingCurrency } =
    useSelectPaymentCurrency();

  // Only show full-page loader on initial load (not on refetch/currency change)
  const isInitialLoading = isOrderLoading || isRequisitesLoading;
  const error = orderError?.message ?? null;
  
  // Show subtle indicator when refetching (currency change, polling)
  const isRefreshing = isOrderFetching && !isOrderLoading;

  const [isUploading, setIsUploading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Copy to clipboard helper
  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      haptic("success");
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopiedField(field);
      haptic("success");
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  // Setup BackButton
  useEffect(() => {
    if (!tg) return;

    tg.BackButton.show();
    const handleBack = () => navigate(-1);
    tg.BackButton.onClick(handleBack);

    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  // Handle currency selection - calls server to update order
  const handleCurrencySelect = useCallback(
    async (currency: string) => {
      if (!orderId || !order || currency === order.currency) return;

      haptic("light");

      try {
        await selectCurrency(orderId, currency);
        haptic("success");
        refetchOrder();
      } catch (err) {
        haptic("error");
        safeShowAlert(getErrorMessage(err, "Ошибка смены валюты"));
      }
    },
    [orderId, order, selectCurrency, refetchOrder]
  );

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    haptic("light");
    setIsUploading(true);

    try {
      // Upload file to S3 via API
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${config.apiUrl}/upload/receipt/${orderId}`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: formData,
        },
      );

      if (!response.ok) {
        let errorMessage = "Ошибка загрузки";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error ?? errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        // Map duplicate receipt (409) to user-friendly Russian message
        if (response.status === 409 || /already been used|another order/i.test(errorMessage)) {
          errorMessage =
            "Этот чек уже был загружен для другого заказа. Загрузите чек именно для этого заказа.";
        }
        throw new Error(errorMessage);
      }

      haptic("success");

      // Краткая пауза, чтобы пользователь увидел успех, затем переход
      await new Promise((r) => setTimeout(r, 400));
      navigate(`/payment/${orderId}/thanks`);
    } catch (uploadError) {
      haptic("error");
      safeShowAlert(getErrorMessage(uploadError));
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const getCurrencySymbol = (currency: string): string => {
    const symbols: Record<string, string> = {
      THB: "฿",
      RUB: "₽",
      USD: "$",
      EUR: "€",
    };
    return symbols[currency] ?? currency;
  };

  if (isInitialLoading) {
    return (
      <Layout>
        <PageLoader message="Загрузка..." />
      </Layout>
    );
  }

  if (error || !order) {
    return (
      <Layout>
        <div className={`${CARD_CLASS} p-4 xs:p-5 sm:p-6 text-center`}>
          <IconAlertTriangle className="w-10 h-10 xs:w-12 xs:h-12 mx-auto mb-3 xs:mb-4 text-amber-500" />
          <h2 className="text-lg xs:text-xl font-bold text-slate-900 mb-2">
            Ошибка
          </h2>
          <p className="text-slate-600 mb-4 xs:mb-6 text-sm">
            {error ?? "Заказ не найден"}
          </p>
          <button onClick={() => navigate("/")} className="btn-primary w-full">
            На главную
          </button>
        </div>
      </Layout>
    );
  }

  // Get pricing from server - use selectedPricing or fall back to order fields
  const selectedPricing = order.selectedPricing;
  const displayCurrency = selectedPricing?.currency ?? order.currency;
  const displaySubtotal = selectedPricing?.subtotal ?? order.subtotal;
  const displayTotal = selectedPricing?.total ?? order.totalAmount;
  const displayFees = selectedPricing?.fees ?? order.fees ?? [];

  const currencySymbol = getCurrencySymbol(displayCurrency);

  // For PENDING_VERIFICATION, redirect to order page (shows "check pending" UI with contact manager)
  if (order.status === "PENDING_VERIFICATION") {
    navigate(`/order/${orderId}`, { replace: true });
    return null;
  }

  // Show status-only page if order is past receipt-upload stage
  const canUploadReceipt = order.status === "AWAITING_PAYMENT";
  if (!canUploadReceipt) {
    return (
      <Layout>
        <div className={`${CARD_CLASS} p-4 xs:p-5 sm:p-6 text-center`}>
          {order.status === "VERIFIED" && (
            <>
              <div className="w-12 h-12 xs:w-14 xs:h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3 xs:mb-4">
                <IconCheck className="w-6 h-6 xs:w-7 xs:h-7 text-green-600" />
              </div>
              <h2 className="text-lg xs:text-xl font-bold text-slate-900 mb-2">
                Платёж подтверждён
              </h2>
              <p className="text-xs xs:text-sm text-slate-500">
                Билеты будут отправлены в бот.
              </p>
            </>
          )}
          {order.status === "TICKET_ISSUED" && (
            <>
              <div className="w-12 h-12 xs:w-14 xs:h-14 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-3 xs:mb-4">
                <IconTicket className="w-6 h-6 xs:w-7 xs:h-7 text-[var(--color-accent)]" />
              </div>
              <h2 className="text-lg xs:text-xl font-bold text-slate-900 mb-2">
                Билеты выданы
              </h2>
              <p className="text-xs xs:text-sm text-slate-500 mb-4 xs:mb-6">
                Проверьте сообщения в боте.
              </p>
              <button
                onClick={() => navigate("/tickets")}
                className="btn-primary w-full"
              >
                Мои билеты
              </button>
            </>
          )}
          {order.status === "REJECTED" && (
            <>
              <div className="w-12 h-12 xs:w-14 xs:h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3 xs:mb-4">
                <IconAlertTriangle className="w-6 h-6 xs:w-7 xs:h-7 text-red-600" />
              </div>
              <h2 className="text-lg xs:text-xl font-bold text-slate-900 mb-2">
                Платёж отклонён
              </h2>
              <p className="text-xs xs:text-sm text-slate-500">
                Свяжитесь с администратором для уточнения деталей.
              </p>
            </>
          )}
        </div>
      </Layout>
    );
  }

  // Get available currencies from order pricing options (server-side calculated)
  // Filter by currencies that have requisites
  const availableCurrencies = (order.pricingOptions ?? [])
    .map((p) => p.currency)
    .filter((c) => requisites.some((r) => r.currency === c));

  const currentRequisites = requisites.find(
    (r) => r.currency === displayCurrency,
  );
  const hasRequisites = !!currentRequisites;

  if (isUploading) {
    return (
      <Layout>
        <PageLoader message="Чек загружается на проверку..." />
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Мероприятие + Состав заказа + Итог — единая карточка (как на странице заказа) */}
      <div className={`${CARD_CLASS} mb-4 lg-mobile:mb-6`}>
        <Link
          to={`/event/${order.event.id}`}
          className="flex items-center gap-3 p-4 xs:p-5 md-mobile:p-6 min-h-[56px] active:bg-slate-50 [-webkit-tap-highlight-color:transparent] no-underline text-inherit"
        >
          <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 shrink-0 flex items-center justify-center">
            {(order.event as { imageUrl?: string | null }).imageUrl ? (
              <img
                src={(order.event as { imageUrl: string }).imageUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100/90 to-slate-100/90 text-slate-400 text-2xl">
                ★
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-medium text-[15px] text-slate-900 truncate">
              {order.event.title}
            </h2>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {new Date(order.event.startsAt).toLocaleDateString("ru-RU", {
                weekday: "short",
                day: "numeric",
                month: "long",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>
          <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
        </Link>

        <div className="border-t border-slate-100">
          <ul className="divide-y divide-slate-100">
            {(() => {
              const itemsSubtotalOriginal = order.items.reduce(
                (sum, i) => sum + parseFloat(i.unitPrice) * i.quantity,
                0,
              );
              const displaySubtotalNum = displaySubtotal ? parseFloat(displaySubtotal) : 0;
              return order.items.map((item) => {
                const itemSubtotalOriginal = parseFloat(item.unitPrice) * item.quantity;
                const itemSubtotal =
                  itemsSubtotalOriginal > 0 && displaySubtotalNum > 0
                    ? (displaySubtotalNum * itemSubtotalOriginal) / itemsSubtotalOriginal
                    : itemSubtotalOriginal;
                return (
                  <li
                    key={item.id}
                    className="flex justify-between items-center px-4 xs:px-5 md-mobile:px-6 py-3 min-h-[48px]"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-[15px] text-slate-900">
                        {item.ticketType.name}
                      </span>
                      <span className="text-slate-400 ml-1.5 text-[13px]">× {item.quantity}</span>
                    </div>
                    <span className="font-semibold text-[15px] text-slate-900 shrink-0">
                      {itemSubtotal.toFixed(2)} {currencySymbol}
                    </span>
                  </li>
                );
              });
            })()}
          </ul>
        </div>

        <div className="px-4 xs:px-5 md-mobile:px-6 py-3 xs:py-4 border-t border-slate-100 space-y-1.5">
          {displaySubtotal && (
            <div className="flex justify-between text-[13px] text-slate-600">
              <span>Билеты</span>
              <span>{displaySubtotal} {currencySymbol}</span>
            </div>
          )}
          {displayFees.length > 0 &&
            displayFees.map((fee) => (
              <div key={fee.id} className="flex justify-between text-[13px] text-slate-500">
                <span>{fee.name}</span>
                <span>{fee.amount} {currencySymbol}</span>
              </div>
            ))}
          <div className="flex justify-between items-center pt-2 mt-2 border-t border-slate-200">
            <span className="font-medium text-[15px] text-slate-900">К оплате</span>
            <span className={`font-bold text-[17px] text-[var(--color-accent)] transition-opacity ${isChangingCurrency || isRefreshing ? "opacity-50" : ""}`}>
              {displayTotal} {currencySymbol}
            </span>
          </div>
        </div>

        <div className="px-4 xs:px-5 md-mobile:px-6 py-3 xs:py-4 border-t border-slate-100 space-y-1">
          <p className="text-[10px] xs:text-xs text-slate-500">Шаг 1 — переведите сумму по реквизитам</p>
          <p className="text-[10px] xs:text-xs font-medium text-teal-700">Шаг 2 — обязательно загрузите чек — без него билеты не выдадут</p>
        </div>

        <div className="px-4 xs:px-5 md-mobile:px-6 pb-4 xs:pb-5 md-mobile:pb-6 border-t border-slate-100">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || !hasRequisites}
            className={`w-full py-3 xs:py-3.5 rounded-xl font-medium flex items-center justify-center gap-1.5 xs:gap-2 transition-all text-sm ${
              isUploading || !hasRequisites
                ? "bg-slate-100 text-slate-400"
                : "bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] shadow-sm hover:shadow-md"
            }`}
          >
            {isUploading ? (
              <>
                <div className="w-4 h-4 xs:w-5 xs:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Загрузка...</span>
              </>
            ) : (
              <>
                <IconCamera className="w-4 h-4 xs:w-5 xs:h-5" />
                <span>Загрузить чек</span>
              </>
            )}
          </button>
          <p className="mt-2 xs:mt-3 text-[10px] xs:text-xs text-slate-400 text-center">
            На чеке должны быть видны сумма, дата и реквизиты
          </p>
        </div>
      </div>

      {availableCurrencies.length === 0 && (
        <div className="rounded-2xl overflow-hidden bg-amber-50/95 shadow-sm border border-amber-200/60 ring-1 ring-amber-200/30 p-3 xs:p-4 sm:p-5 mb-4 lg-mobile:mb-6">
          <p className="text-xs xs:text-sm text-teal-800">
            Реквизиты не настроены. Обратитесь к администратору.
          </p>
        </div>
      )}

      {hasRequisites && currentRequisites && (
        <div className="mb-4 lg-mobile:mb-6">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">Реквизиты для перевода</p>
          <p className="text-[11px] xs:text-xs text-slate-400 mb-3">Нажмите на строку, чтобы скопировать</p>
          <div className={`${CARD_CLASS}`}>
            {availableCurrencies.length > 1 && (
              <div className="px-4 xs:px-5 md-mobile:px-6 py-3 border-b border-slate-100 flex justify-center">
                <div className="flex gap-2 xs:gap-3 flex-1 max-w-xs">
                  {availableCurrencies.map((currency) => (
                    <button
                      key={currency}
                      onClick={() => handleCurrencySelect(currency)}
                      disabled={isChangingCurrency}
                      className={`flex-1 py-2.5 xs:py-3 rounded-xl text-xs xs:text-sm font-medium transition-all ${
                        displayCurrency === currency
                          ? "bg-[var(--color-accent)] text-white shadow-md"
                          : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"
                      } ${isChangingCurrency ? "opacity-50" : ""}`}
                    >
                      {getCurrencySymbol(currency)} {currency}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="divide-y divide-slate-100">
              <button
                type="button"
                onClick={() => copyToClipboard(currentRequisites.bank, "bank")}
                className="w-full text-left px-4 xs:px-5 md-mobile:px-6 py-3 min-h-[48px] hover:bg-slate-50/80 active:bg-slate-100 transition-colors [-webkit-tap-highlight-color:transparent]"
              >
                <div className="flex justify-between items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] xs:text-xs text-slate-400 uppercase tracking-wide mb-0.5">Банк</p>
                    <p className="font-medium text-[15px] text-slate-900 truncate">{currentRequisites.bank}</p>
                  </div>
                  <span className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-slate-100 text-slate-500">
                    {copiedField === "bank" ? (
                      <IconCheck className="w-4 h-4 xs:w-5 xs:h-5 text-green-500" />
                    ) : (
                      <IconCopy className="w-4 h-4 xs:w-5 xs:h-5" />
                    )}
                  </span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => copyToClipboard(currentRequisites.account, "account")}
                className="w-full text-left px-4 xs:px-5 md-mobile:px-6 py-3 min-h-[48px] hover:bg-slate-50/80 active:bg-slate-100 transition-colors [-webkit-tap-highlight-color:transparent]"
              >
                <div className="flex justify-between items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] xs:text-xs text-slate-400 uppercase tracking-wide mb-0.5">Счёт / карта</p>
                    <p className="font-mono font-semibold text-[15px] text-slate-900 break-all leading-relaxed">{currentRequisites.account}</p>
                  </div>
                  <span className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-slate-100 text-slate-500">
                    {copiedField === "account" ? (
                      <IconCheck className="w-4 h-4 xs:w-5 xs:h-5 text-green-500" />
                    ) : (
                      <IconCopy className="w-4 h-4 xs:w-5 xs:h-5" />
                    )}
                  </span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => copyToClipboard(currentRequisites.name, "name")}
                className="w-full text-left px-4 xs:px-5 md-mobile:px-6 py-3 min-h-[48px] hover:bg-slate-50/80 active:bg-slate-100 transition-colors [-webkit-tap-highlight-color:transparent]"
              >
                <div className="flex justify-between items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] xs:text-xs text-slate-400 uppercase tracking-wide mb-0.5">Получатель</p>
                    <p className="font-medium text-[15px] text-slate-900 truncate">{currentRequisites.name}</p>
                  </div>
                  <span className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-slate-100 text-slate-500">
                    {copiedField === "name" ? (
                      <IconCheck className="w-4 h-4 xs:w-5 xs:h-5 text-green-500" />
                    ) : (
                      <IconCopy className="w-4 h-4 xs:w-5 xs:h-5" />
                    )}
                  </span>
                </div>
              </button>
            </div>

            <div className="p-4 xs:p-5 md-mobile:p-6 border-t border-slate-100">
              <button
                type="button"
                onClick={() =>
                  copyToClipboard(
                    `${currentRequisites.bank}\n${currentRequisites.account}\n${currentRequisites.name}`,
                    "all",
                  )
                }
                className="w-full flex items-center justify-center gap-2 py-3 xs:py-3.5 rounded-xl text-sm font-medium text-[var(--color-accent)] bg-teal-50 hover:bg-teal-100 active:bg-teal-200 transition-colors min-h-[44px] xs:min-h-[48px]"
              >
                {copiedField === "all" ? (
                  <>
                    <IconCheck className="w-4 h-4 xs:w-5 xs:h-5" />
                    <span>Скопировано!</span>
                  </>
                ) : (
                  <>
                    <IconCopy className="w-4 h-4 xs:w-5 xs:h-5" />
                    <span>Скопировать всё</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
